/**
 * Web Crypto Adapter - Browser Web Crypto API implementation
 * @module @one-payments/adapters-web
 */

import type { CryptoAdapter } from '@one-payments/core';

/**
 * Web Crypto Adapter using Web Crypto API with strict typing
 */
export class WebCryptoAdapter implements CryptoAdapter {
  public async sha256(input: string | Uint8Array): Promise<string> {
    const data = typeof input === 'string' ? new TextEncoder().encode(input) : input;
    const hashBuffer = await crypto.subtle.digest('SHA-256', data as BufferSource);
    return this.bufferToHex(hashBuffer);
  }

  public async hmacSHA256(secret: string, message: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);
    const messageData = encoder.encode(message);

    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', key, messageData);
    return this.bufferToHex(signature);
  }

  public uuid(): string {
    return crypto.randomUUID();
  }

  public randomBytes(length: number): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(length));
  }

  private bufferToHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
